'use client'

// Всплывающая подсказка «что это» для вкладок и ссылок дашборда (просьба
// пользователя 2026-09-17). Тот же стиль карточки, что у перевода по
// наведению (tr-text.tsx): тёмный градиент, золотая рамка, ледяной заголовок.
//
// Десктоп: показывается при наведении (с задержкой, чтобы не мелькать при
// проходе мышью), прячется при уходе. Телефон: долгое нажатие показывает
// подсказку и НЕ открывает ссылку; обычный тап работает как раньше.
// Одна подсказка на страницу: новая закрывает предыдущую.

import { createPortal } from 'react-dom'
import { PopoverArrow, htmlZoom, placePopover, type PopoverSide } from '@/components/popover-arrow'
import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react'

const HOVER_DELAY_MS = 320
const HIDE_DELAY_MS = 180
const PRESS_MS = 450
const WIDTH = 300
const AUTO_SHOW_DELAY_MS = 900
const AUTO_SHOW_MS = 4500

const HOVER_QUERY = '(hover: hover) and (pointer: fine)'
function subscribeHover(cb: () => void) {
  const mq = window.matchMedia(HOVER_QUERY)
  mq.addEventListener('change', cb)
  return () => mq.removeEventListener('change', cb)
}
const useCanHover = () => useSyncExternalStore(subscribeHover, () => window.matchMedia(HOVER_QUERY).matches, () => false)

const C = {
  bg: 'linear-gradient(165deg, oklch(0.27 0.05 254 / 0.98) 0%, oklch(0.17 0.035 254 / 0.99) 100%)',
  border: 'oklch(0.78 0.14 228 / 0.55)',
  text: 'oklch(0.97 0.005 240)',
  muted: 'oklch(0.8 0.03 240)',
  ice: 'oklch(0.86 0.11 225)',
}

// ── Одна открытая подсказка на приложение ──
type Rect = { left: number; top: number; bottom: number; width: number }
type Open = { id: string; title: string; text: string; rect: Rect; pinned: boolean }
let current: Open | null = null
const listeners = new Set<() => void>()
function setCurrent(next: Open | null) {
  current = next
  listeners.forEach((l) => l())
}
function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

let hideTimer: ReturnType<typeof setTimeout> | null = null
function cancelHide() {
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = null
}
function scheduleHide(id: string) {
  cancelHide()
  hideTimer = setTimeout(() => current?.id === id && setCurrent(null), HIDE_DELAY_MS)
}

const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect()
  return { left: r.left, top: r.top, bottom: r.bottom, width: r.width }
}

export function Hint({
  title,
  text,
  children,
  style,
  className,
  tap = false,
  autoShowKey,
}: {
  title: string
  text: string
  children: ReactNode
  style?: CSSProperties
  className?: string
  // Значок без своего действия (вопросик): на телефоне открывается обычным
  // тапом, повторный тап закрывает. Для ссылок и кнопок остаётся долгое нажатие.
  tap?: boolean
  // Показать подсказку самой один раз на устройстве (localStorage под этим ключом):
  // для кнопок-значков без подписи, например микрофона «Записать себя» (15.10.2026).
  // До 22.10.2026 был sessionStorage, но в приложении на телефоне он чистится при
  // каждом открытии, и подсказка закрывала фразу на каждом занятии (пользователь).
  autoShowKey?: string
}) {
  const id = useId()
  const wrapRef = useRef<HTMLDivElement>(null)
  const canHover = useCanHover()
  const active = useSyncExternalStore(subscribe, () => current?.id === id, () => false)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pressed = useRef(false)

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
      if (pressTimer.current) clearTimeout(pressTimer.current)
      if (current?.id === id) setCurrent(null)
    },
    [id],
  )

  const open = (el: Element, pinned: boolean) => {
    cancelHide()
    setCurrent({ id, title, text, rect: rectOf(el), pinned })
  }

  useEffect(() => {
    if (!autoShowKey) return
    try {
      if (localStorage.getItem(autoShowKey)) return
    } catch {
      return
    }
    const show = setTimeout(() => {
      const el = wrapRef.current
      if (!el || current) return
      try {
        localStorage.setItem(autoShowKey, '1')
      } catch {}
      open(el, false)
    }, AUTO_SHOW_DELAY_MS)
    const hide = setTimeout(() => current?.id === id && !current.pinned && setCurrent(null), AUTO_SHOW_DELAY_MS + AUTO_SHOW_MS)
    return () => {
      clearTimeout(show)
      clearTimeout(hide)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoShowKey])

  return (
    <>
      <div
        ref={wrapRef}
        data-tk-hint=""
        className={className}
        // grid: единственный потомок (ссылка, кнопка) растягивается на всю
        // ячейку — обёртка не меняет раскладку сетки дашборда.
        style={{ display: 'grid', minWidth: 0, ...style }}
        onMouseEnter={
          canHover
            ? (e) => {
                const el = e.currentTarget
                if (hoverTimer.current) clearTimeout(hoverTimer.current)
                hoverTimer.current = setTimeout(() => open(el, false), HOVER_DELAY_MS)
              }
            : undefined
        }
        onMouseLeave={
          canHover
            ? () => {
                if (hoverTimer.current) clearTimeout(hoverTimer.current)
                if (current?.id === id && !current.pinned) scheduleHide(id)
              }
            : undefined
        }
        onPointerDown={
          canHover
            ? undefined
            : (e) => {
                if (e.pointerType === 'mouse') return
                const el = e.currentTarget
                pressed.current = false
                if (pressTimer.current) clearTimeout(pressTimer.current)
                pressTimer.current = setTimeout(() => {
                  pressed.current = true
                  open(el, true)
                }, PRESS_MS)
              }
        }
        onPointerUp={() => pressTimer.current && clearTimeout(pressTimer.current)}
        onPointerCancel={() => pressTimer.current && clearTimeout(pressTimer.current)}
        onPointerMove={() => pressTimer.current && clearTimeout(pressTimer.current)}
        onClick={
          tap
            ? (e) => {
                if (current?.id === id) setCurrent(null)
                else open(e.currentTarget, true)
              }
            : undefined
        }
        onClickCapture={(e) => {
          // Долгое нажатие показало подсказку — этот тап ссылку не открывает.
          if (pressed.current) {
            e.preventDefault()
            e.stopPropagation()
            pressed.current = false
          }
        }}
        onContextMenu={(e) => {
          if (!canHover) e.preventDefault()
        }}
      >
        {children}
      </div>
      {active && current && <HintCard state={current} />}
    </>
  )
}

function HintCard({ state }: { state: Open }) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number; arrowX: number; side: PopoverSide } | null>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setPos(placePopover(state.rect, Math.min(WIDTH, window.innerWidth / htmlZoom() - 24), el.offsetHeight, 10))
  }, [state])

  useEffect(() => {
    function onDown(e: PointerEvent) {
      const t = e.target as Node
      if (ref.current?.contains(t)) return
      if (t instanceof Element && t.closest('[data-tk-hint]')) return
      setCurrent(null)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setCurrent(null)
    }
    function onScroll() {
      setCurrent(null)
    }
    document.addEventListener('pointerdown', onDown, true)
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [])

  if (typeof document === 'undefined') return null
  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      onMouseEnter={cancelHide}
      onMouseLeave={() => !state.pinned && scheduleHide(state.id)}
      style={{
        position: 'fixed',
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        zIndex: 9999,
        width: `min(${WIDTH}px, calc(var(--tk-vw, 100vw) - 24px))`,
        padding: '11px 14px',
        borderRadius: 14,
        border: `1px solid ${C.border}`,
        background: C.bg,
        boxShadow:
          'inset 0 1px 0 oklch(1 0 0 / 0.07), 0 0 0 1px oklch(0.58 0.19 258 / 0.25), 0 16px 34px -12px oklch(0 0 0 / 0.8), 0 0 24px -6px oklch(0.58 0.19 258 / 0.45)',
        color: C.text,
        fontSize: 15,
        lineHeight: 1.45,
        textAlign: 'left',
        visibility: pos ? 'visible' : 'hidden',
        pointerEvents: state.pinned ? 'auto' : 'none',
      }}
    >
      {pos && <PopoverArrow x={pos.arrowX} side={pos.side} border={C.border} />}
      <div style={{ fontSize: 15.5, fontWeight: 700, color: C.ice, marginBottom: 3 }}>{state.title}</div>
      <div style={{ color: C.muted, whiteSpace: 'pre-line' }}>{state.text}</div>
    </div>,
    document.body,
  )
}
